import React from 'react';
import PropTypes from 'prop-types';
import {
  FastField,
  useFormikContext,
} from 'formik';
import {
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import fieldName, {
  MIN_SELECTION_LIMIT,
  questionFieldName,
} from '../../../../formFields';
import externalText from '../../../../texts';
import {
  Container,
  SingleRowContainer,
  ShortFormInput,
  LongFormInput,
  StyledTypography,
} from '../styles';

import { FormType } from '../../../../../../Model/Form';
import texts from './texts.json';

const selectionLimitInputProps = {
  min: MIN_SELECTION_LIMIT,
  step: MIN_SELECTION_LIMIT,
};

const OptionsQuestion = ({
  index, formType, isCoachView, disabled,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const isPostPaymentForm = formType === FormType.POST_PAYMENT;
  const fieldBaseName = `${fieldName.FIELDS}.${index}`;
  const isMultiple = !!values[fieldName.FIELDS][index][questionFieldName.ALLOW_MULTIPLE];
  const shouldShowSelectionLimits = isPostPaymentForm && isMultiple;
  const minSelectionsFieldName = `${fieldBaseName}.${questionFieldName.MIN_SELECTIONS}`;
  const maxSelectionsFieldName = `${fieldBaseName}.${questionFieldName.MAX_SELECTIONS}`;

  return (
    <Container>
      <SingleRowContainer>
        {!isCoachView && (
          <ShortFormInput
            name={`${fieldBaseName}.${questionFieldName.CUSTOM_CLASS}`}
            label={externalText.labels[questionFieldName.CUSTOM_CLASS]}
            disabled={disabled}
          />
        )}
        <FastField name={`${fieldBaseName}.${questionFieldName.REQUIRED}`}>
          {({ field }) => (
            <FormControlLabel
              key={`${fieldBaseName}-${index}-${questionFieldName.REQUIRED}`}
              control={<Checkbox checked={field.value} />}
              label={externalText.labels[questionFieldName.REQUIRED]}
              name={questionFieldName.REQUIRED}
              disabled={disabled}
              onChange={(event) => {
                setFieldValue(`${fieldBaseName}.${questionFieldName.REQUIRED}`, event.target.checked);
              }}
            />
          )}
        </FastField>
      </SingleRowContainer>
      <SingleRowContainer>
        {!isCoachView && (
          <ShortFormInput
            name={`${fieldBaseName}.${questionFieldName.ID}`}
            label={externalText.labels[questionFieldName.ID]}
            disabled={disabled}
          />
        )}
        <FastField name={`${fieldBaseName}.${questionFieldName.ALLOW_MULTIPLE}`}>
          {({ field }) => (
            <FormControlLabel
              key={`${fieldBaseName}-${index}-${questionFieldName.ALLOW_MULTIPLE}`}
              control={<Checkbox checked={field.value} />}
              label={externalText.labels[questionFieldName.ALLOW_MULTIPLE]}
              name={questionFieldName.ALLOW_MULTIPLE}
              disabled={disabled}
              onChange={(event) => {
                const { checked } = event.target;
                setFieldValue(`${fieldBaseName}.${questionFieldName.ALLOW_MULTIPLE}`, checked);
                if (!checked) {
                  setFieldValue(minSelectionsFieldName, '');
                  setFieldValue(maxSelectionsFieldName, '');
                }
              }}
            />
          )}
        </FastField>
        {!isPostPaymentForm && (
          <FastField name={`${fieldBaseName}.${questionFieldName.DISPLAY_AS_CHECKBOXES}`}>
            {({ field }) => (
              <FormControlLabel
                key={`${fieldBaseName}-${index}-${questionFieldName.DISPLAY_AS_CHECKBOXES}`}
                control={<Checkbox checked={field.value} />}
                label={externalText.labels[questionFieldName.DISPLAY_AS_CHECKBOXES]}
                name={questionFieldName.DISPLAY_AS_CHECKBOXES}
                disabled={disabled}
                onChange={(event) => {
                  setFieldValue(`${fieldBaseName}.${questionFieldName.DISPLAY_AS_CHECKBOXES}`, event.target.checked);
                }}
              />
            )}
          </FastField>
        )}
      </SingleRowContainer>
      {shouldShowSelectionLimits && (
        <SingleRowContainer>
          <ShortFormInput
            name={minSelectionsFieldName}
            label={externalText.labels[questionFieldName.MIN_SELECTIONS]}
            disabled={disabled}
            type="number"
            inputProps={selectionLimitInputProps}
          />
          <ShortFormInput
            name={maxSelectionsFieldName}
            label={externalText.labels[questionFieldName.MAX_SELECTIONS]}
            disabled={disabled}
            type="number"
            inputProps={selectionLimitInputProps}
          />
        </SingleRowContainer>
      )}
      <LongFormInput
        name={`${fieldBaseName}.${questionFieldName.LABEL}`}
        label={externalText.labels[questionFieldName.LABEL]}
        disabled={disabled}
      />
      {isPostPaymentForm && (
        <LongFormInput
          name={`${fieldBaseName}.${questionFieldName.SUB_TEXT}`}
          label={externalText.labels[questionFieldName.SUB_TEXT]}
          disabled={disabled}
        />
      )}
      <LongFormInput
        name={`${fieldBaseName}.${questionFieldName.OPTIONS}`}
        label={externalText.labels[questionFieldName.OPTIONS]}
        disabled={disabled}
      />
      <StyledTypography $withMargin>{texts.optionsDescription}</StyledTypography>
      <ShortFormInput
        name={`${fieldBaseName}.${questionFieldName.PROMPT_INPUT}`}
        label={externalText.labels[questionFieldName.PROMPT_INPUT]}
        disabled={disabled}
      />
      <StyledTypography>{texts.promptInputDescription}</StyledTypography>
    </Container>
  );
};

OptionsQuestion.propTypes = {
  index: PropTypes.number.isRequired,
  formType: PropTypes.string.isRequired,
  isCoachView: PropTypes.bool,
  disabled: PropTypes.bool,
};

OptionsQuestion.defaultProps = {
  isCoachView: false,
  disabled: false,
};

export default OptionsQuestion;
